import {
  Stack, Group, Text, Box, Badge, TextInput, Textarea, Select, Autocomplete,
  Button, SimpleGrid, Divider, Skeleton,
} from '@mantine/core'
import { JOB_TITLES_TECH } from '@/constants/jobTitles'
import { notifications } from '@mantine/notifications'
import { PhoneInputIntl } from '@/components/ui/PhoneInputIntl'
import { CepInput } from '@/components/ui/CepInput'
import { IconDeviceFloppy, IconRotateClockwise } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { profileApi, type ProfileUpdatePayload } from '@/api/profile'
import { useAuthStore } from '@/store/auth'
import { formatApiError } from '@/api/errors'
import { WidgetCard } from '@/components/ui'
import { TimezoneSelect } from '@/components/ui/TimezoneSelect'
import { AvatarUpload } from './AvatarUpload'

interface FormState {
  full_name: string
  job_title: string
  phone: string
  employee_number: string
  organization_id: string  // Select usa string
  address: string
  timezone: string  // '' = usar default do tenant/sistema
  // Endereço estruturado (D-UX3 / MIGRATION_139)
  cep: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cidade: string
  uf: string
}

function _formFromUser(user: any): FormState {
  return {
    full_name: user?.full_name ?? '',
    job_title: user?.job_title ?? '',
    phone: user?.phone ?? '',
    employee_number: user?.employee_number ?? '',
    organization_id: user?.organization_id ? String(user.organization_id) : '',
    address: user?.address ?? '',
    timezone: user?.timezone ?? '',
    cep: user?.cep ?? '',
    logradouro: user?.logradouro ?? '',
    numero: user?.numero ?? '',
    complemento: user?.complemento ?? '',
    bairro: user?.bairro ?? '',
    cidade: user?.cidade ?? '',
    uf: user?.uf ?? '',
  }
}

function _initials(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return email.slice(0, 2).toUpperCase()
}

export function IdentidadeCard() {
  const qc = useQueryClient()
  const { user, refreshUser } = useAuthStore()

  const [form, setForm] = useState<FormState>(() => _formFromUser(user))
  const initialForm = useMemo(() => _formFromUser(user), [user])

  // Sincroniza form quando user mudar (apos refreshUser)
  useEffect(() => { setForm(_formFromUser(user)) }, [user])

  const orgsQuery = useQuery({
    queryKey: ['organizations', 'lite'],
    queryFn: profileApi.organizationsLite,
    staleTime: 5 * 60 * 1000,  // 5 min
  })

  const updateMutation = useMutation({
    mutationFn: (payload: ProfileUpdatePayload) => profileApi.update(payload),
    onSuccess: () => {
      notifications.show({ color: 'teal', title: 'Salvo', message: 'Perfil atualizado.' })
      qc.invalidateQueries({ queryKey: ['me'] })
      refreshUser()
    },
    onError: (err) => {
      notifications.show({ color: 'red', title: 'Erro ao salvar', message: formatApiError(err) })
    },
  })

  if (!user) {
    return (
      <WidgetCard p="md">
        <Skeleton h={200} />
      </WidgetCard>
    )
  }

  const initials = _initials(form.full_name || user.full_name || '', user.email)
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm)

  const handleSave = () => {
    if (!form.full_name.trim()) {
      notifications.show({ color: 'red', message: 'Nome completo e obrigatório.' })
      return
    }
    // Diff: so envia campos que mudaram
    const payload: ProfileUpdatePayload = {}
    if (form.full_name !== initialForm.full_name) payload.full_name = form.full_name.trim()
    if (form.job_title !== initialForm.job_title) payload.job_title = form.job_title.trim()
    if (form.phone !== initialForm.phone) payload.phone = form.phone.trim()
    if (form.employee_number !== initialForm.employee_number) payload.employee_number = form.employee_number.trim()
    if (form.address !== initialForm.address) payload.address = form.address.trim()
    for (const _f of ['cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'] as const) {
      if (form[_f] !== initialForm[_f]) payload[_f] = form[_f].trim()
    }
    if (form.organization_id !== initialForm.organization_id) {
      payload.organization_id = form.organization_id ? Number(form.organization_id) : null
    }
    if (form.timezone !== initialForm.timezone) {
      payload.timezone = form.timezone  // '' limpa (volta a usar tenant/server)
    }
    updateMutation.mutate(payload)
  }

  const handleReset = () => setForm(initialForm)

  return (
    <WidgetCard p="md">
      <Stack gap="md">
        {/* Header: avatar + identidade */}
        <Group align="flex-start" gap="lg" wrap="nowrap">
          <AvatarUpload
            initials={initials}
            avatarUrl={user.avatar_url}
            supported={user.avatar_supported ?? false}
          />
          <Box style={{ flex: 1 }}>
            <Text size="lg" fw={700}>{user.full_name || user.email.split('@')[0]}</Text>
            <Text size="sm" c="dimmed">{user.email}</Text>
            <Group gap="xs" mt={6}>
              <Badge variant="light" color="inovareTeal" size="sm">LDAP</Badge>
              <Badge variant="light" color="gray" size="sm">{user.tenants?.length ?? 0} tenants</Badge>
              {user.roles?.slice(0, 3).map(r => (
                <Badge key={r.code} variant="light" color="blue" size="sm">{r.name}</Badge>
              ))}
              {(user.roles?.length ?? 0) > 3 && (
                <Badge variant="light" color="gray" size="sm">+{(user.roles!.length) - 3}</Badge>
              )}
            </Group>
          </Box>
        </Group>

        <Divider />

        {/* Form de campos editaveis */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label="Nome completo"
            required
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.currentTarget.value })}
            maxLength={200}
          />
          <Autocomplete
            label="Cargo"
            data={[...JOB_TITLES_TECH]}
            value={form.job_title}
            onChange={(value) => setForm({ ...form, job_title: value })}
            maxLength={100}
            placeholder="Comece a digitar ou escolha da lista"
          />
          <PhoneInputIntl
            label="Telefone"
            value={form.phone}
            onChange={(e164) => setForm({ ...form, phone: e164 })}
          />
          <TextInput
            label="Matrícula"
            value={form.employee_number}
            onChange={(e) => setForm({ ...form, employee_number: e.currentTarget.value })}
            maxLength={50}
          />
          <Select
            label="Organização"
            value={form.organization_id || null}
            onChange={(v) => setForm({ ...form, organization_id: v ?? '' })}
            data={(orgsQuery.data ?? []).map(o => ({ value: String(o.id), label: o.name }))}
            placeholder={orgsQuery.isLoading ? 'Carregando...' : 'Selecionar organização'}
            disabled={orgsQuery.isLoading}
            clearable
            searchable
          />
          <Textarea
            label="Endereço (livre)"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.currentTarget.value })}
            maxLength={500}
            minRows={2}
            autosize
            maxRows={4}
            placeholder="Rua, número, bairro, cidade"
          />
          {/* Endereço estruturado (D-UX3) — CEP preenche logradouro/bairro/cidade/uf via ViaCEP (editáveis). */}
          <CepInput
            value={form.cep}
            onChange={(cep) => setForm((f) => ({ ...f, cep }))}
            onResolved={(a) => setForm((f) => ({
              ...f, logradouro: a.logradouro, bairro: a.bairro, cidade: a.cidade, uf: a.uf,
            }))}
          />
          <TextInput
            label="Logradouro"
            value={form.logradouro}
            onChange={(e) => setForm({ ...form, logradouro: e.currentTarget.value })}
            maxLength={200}
          />
          <Group grow>
            <TextInput
              label="Número"
              value={form.numero}
              onChange={(e) => setForm({ ...form, numero: e.currentTarget.value })}
              maxLength={20}
            />
            <TextInput
              label="Complemento"
              value={form.complemento}
              onChange={(e) => setForm({ ...form, complemento: e.currentTarget.value })}
              maxLength={100}
            />
          </Group>
          <TextInput
            label="Bairro"
            value={form.bairro}
            onChange={(e) => setForm({ ...form, bairro: e.currentTarget.value })}
            maxLength={100}
          />
          <Group grow>
            <TextInput
              label="Cidade"
              value={form.cidade}
              onChange={(e) => setForm({ ...form, cidade: e.currentTarget.value })}
              maxLength={100}
            />
            <TextInput
              label="UF"
              maxLength={2}
              value={form.uf}
              onChange={(e) => setForm({ ...form, uf: e.currentTarget.value.toUpperCase() })}
            />
          </Group>
          <TimezoneSelect
            value={form.timezone}
            onChange={(v) => setForm({ ...form, timezone: v ?? '' })}
            description="Usado pra avaliar restrições de horário em apps configuradas com 'Usar fuso do usuário'."
          />
        </SimpleGrid>

        {/* Acoes — so aparecem se ha mudanca */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle" color="gray" size="sm"
            type="button"
            disabled={!dirty || updateMutation.isPending}
            leftSection={<IconRotateClockwise size={14} />}
            onClick={handleReset}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            type="button"
            disabled={!dirty}
            loading={updateMutation.isPending}
            leftSection={<IconDeviceFloppy size={14} />}
            onClick={handleSave}
          >
            Salvar
          </Button>
        </Group>
      </Stack>
    </WidgetCard>
  )
}
